import React, { useState } from 'react';
import { getLocalISOString } from '../utils/dateUtils';
import { useForm } from 'react-hook-form';
import * as Icons from 'lucide-react';
import toast from 'react-hot-toast';
import { computeCollectionAmounts, isCreditOrder } from '../utils/payments';
import MultiplePaymentEvidenceUpload from './MultiplePaymentEvidenceUpload';

const DeliveryRegistrationModal = ({ isOpen, onClose, order, onConfirm }) => {
  const [deliveryPhoto, setDeliveryPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null); // 'payment' | 'delivery' | null
  const [gps, setGps] = useState({ latitude: null, longitude: null, loading: false, error: null });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    setValue
  } = useForm();

  // Estados "antibobos" para decisiones rápidas
  const [productChoice, setProductChoice] = useState(null); // 'efectivo' | 'transferencia' | null
  const [feeChoice, setFeeChoice] = useState(null); // 'efectivo' | 'transferencia' | null

  // Helper para obtener el monto correcto según el endpoint usado
  const getOrderAmount = (order) => {
    // Para mensajeros, el campo se llama 'total'
    return parseFloat(order?.total || order?.total_amount || 0);
  };

  const formatCOP = (n) =>
    (Number(n || 0)).toLocaleString('es-CO', { minimumFractionDigits: 0 });

  // Detección simple del canal desde un texto (local para no exponer campos al mensajero)
  const detectProviderFromStringLocal = (text = '') => {
    const t = String(text).toLowerCase();
    if (t.includes('nequi')) return 'nequi';
    if (t.includes('daviplata')) return 'daviplata';
    if (t.includes('bancolombia') || t.includes('banco')) return 'bancolombia';
    return '';
  };

  // expectedDeliveryFee computed via computeCollectionAmounts below

  // Selección de métodos de pago (por defecto desde el pedido) - definido más abajo después de calcular requiresPayment

  // Cálculo usando helpers centralizados
  const { productDue, shippingDue, totalDue } = computeCollectionAmounts(order);
  const isNoCharge = (totalDue || 0) <= 0;
  const requiresPayment = !isNoCharge && productDue > 0;
  const expectedAmount = productDue;
  const shouldCollectDeliveryFee = shippingDue > 0;
  const feeToCollect = shippingDue;
  const expectedDeliveryFee = shippingDue;
  // Guardado adicional para evitar renderizar secciones de cobro cuando no aplica
  const showPaymentSections = !isNoCharge && (expectedAmount > 0);

  // Selección de métodos de pago (por defecto desde el pedido)
  // Importante: definir DESPUÉS de requiresPayment para evitar usar la variable antes de inicializarla
  const productPaymentMethod = (requiresPayment ? (watch('productPaymentMethod') || (order?.payment_method || 'efectivo')) : 'efectivo').toLowerCase();
  const feePaymentMethod = (watch('deliveryFeePaymentMethod') || 'efectivo').toLowerCase();

  // Normalizador local y pago del pedido normalizado
  const normalize = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '').trim();
  const orderPaymentNorm = normalize(order?.payment_method);

  // Forzar efectivo también para contraentrega
  const forceCashProduct = false; // Permitir al encargado de la entrega cambiar a transferencia

  const amountReceived = watch('amountReceived');
  const transferAmount = watch('transferAmount');
  const amountMatch = (() => {
    if (['efectivo', 'contraentrega'].includes(productPaymentMethod)) {
      const received = parseFloat(amountReceived || 0);
      const diff = Math.abs(received - expectedAmount);
      return diff <= 200; // Tolerancia de +/- 200 pesos
    }
    return true;
  })();


  // Registrar campos ocultos para que formen parte del submit
  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    register('transferBank');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    register('transferReference');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    register('transferDate');
  }, [register]);

  // Autocompletar metadatos electrónicos (ocultos) para cumplir validaciones backend sin mostrar campos
  React.useEffect(() => {
    if (!isNoCharge && (productPaymentMethod === 'transferencia')) {
      // Banco/canal: intentar desde order.electronic_payment_type, detectar desde notas o fallback 'otro'
      const fromOrder = (order?.electronic_payment_type || '').toString().trim();
      const detected = detectProviderFromStringLocal(order?.notes || '') || '';
      const bank = (fromOrder || detected || 'otro').toLowerCase();
      const ref = `auto-${Date.now()}`;
      const dateStr = getLocalISOString().slice(0, 10); // YYYY-MM-DD
      setValue('transferBank', bank, { shouldValidate: false });
      setValue('transferReference', ref, { shouldValidate: false });
      setValue('transferDate', dateStr, { shouldValidate: false });
    } else {
      // Limpiar si cambia a efectivo o cuando no se requiere cobro
      setValue('transferBank', null, { shouldValidate: false });
      setValue('transferReference', null, { shouldValidate: false });
      setValue('transferDate', null, { shouldValidate: false });
    }
  }, [isNoCharge, requiresPayment, productPaymentMethod, order, setValue]);
  const deliveryFeeCollected = watch('deliveryFeeCollected');
  // Campos de "Entrega con confianza"
  const trustedDelivery = watch('trustedDelivery');
  const authorizedByName = watch('authorizedByName');
  const trustNote = watch('trustNote');

  // Reglas de evidencia: pedir foto de pago para efectivo o contraentrega
  const requiresPaymentEvidence = false; // Now handled by MultiplePaymentEvidenceUpload component


  // Acciones rápidas para decisiones "antibobos"
  const handleProductQuickSelect = (method) => {
    if (method === 'efectivo') {
      setProductChoice('efectivo');
      setValue('productPaymentMethod', 'efectivo', { shouldValidate: true });
      setValue('amountReceived', expectedAmount, { shouldValidate: true });
      setValue('transferAmount', 0, { shouldValidate: true });
    } else if (method === 'transferencia') {
      setProductChoice('transferencia');
      setValue('productPaymentMethod', 'transferencia', { shouldValidate: true });
      setValue('amountReceived', 0, { shouldValidate: true });
      setValue('transferAmount', expectedAmount, { shouldValidate: true });
    }
  };

  const handleFeeQuickSelect = (method) => {
    if (!shouldCollectDeliveryFee) return;
    if (method === 'efectivo') {
      setFeeChoice('efectivo');
      setValue('deliveryFeePaymentMethod', 'efectivo', { shouldValidate: true });
      setValue('deliveryFeeCollected', expectedDeliveryFee, { shouldValidate: true });
    } else {
      setFeeChoice('transferencia');
      setValue('deliveryFeePaymentMethod', 'transferencia', { shouldValidate: true });
      setValue('deliveryFeeCollected', 0, { shouldValidate: true });
    }
  };

  const handleClose = () => {
    reset();
    setDeliveryPhoto(null);
    setZoomedImage(null);
    onClose();
  };

  // Efecto para capturar GPS al abrir el modal
  React.useEffect(() => {
    if (isOpen) {
      captureGPS();
    } else {
      setGps({ latitude: null, longitude: null, loading: false, error: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const captureGPS = () => {
    if (!navigator.geolocation) {
      setGps(prev => ({ ...prev, error: 'No soportado' }));
      return;
    }

    setGps(prev => ({ ...prev, loading: true, error: null }));
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          loading: false,
          error: null
        });
        console.log('📍 GPS captured in modal:', pos.coords.latitude, pos.coords.longitude);
      },
      (err) => {
        console.warn('📍 GPS error in modal:', err);
        let msg = 'Error';
        if (err.code === 1) msg = 'Permiso denegado';
        else if (err.code === 2) msg = 'No disponible';
        else if (err.code === 3) msg = 'Tiempo agotado';
        setGps(prev => ({ ...prev, loading: false, error: msg }));
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleFileChange = (event, type) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 15 * 1024 * 1024) { // 15MB limit
        toast.error('El archivo no puede ser mayor a 15MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        // Payment photos now handled by MultiplePaymentEvidenceUpload
        if (type === 'delivery') {
          setDeliveryPhoto({
            file,
            preview: e.target.result
          });
        }
      };
      reader.readAsDataURL(file);
      // Reset input value to allow same file re-selection if needed
      event.target.value = '';
    }
  };



  const onSubmit = async (data) => {
    // Validaciones dinámicas según método de pago seleccionado por el mensajero
    if (!isNoCharge && requiresPayment) {
      if (['efectivo', 'contraentrega'].includes(productPaymentMethod)) {
        if (!amountMatch) {
          toast.error('El monto cobrado debe coincidir exactamente con el valor a cobrar');
          return;
        }
      }
    }

    // Reglas adicionales para entrega con confianza
    if (trustedDelivery) {
      if (!authorizedByName || String(authorizedByName).trim().length < 3) {
        toast.error('Debes ingresar el nombre de la persona que autoriza la entrega con confianza');
        return;
      }
      if (!trustNote || String(trustNote).trim().length < 3) {
        toast.error('Debes ingresar una nota/motivo para la entrega con confianza');
        return;
      }
    }



    if (!deliveryPhoto) {
      toast.error('Debe tomar una foto como evidencia de entrega');
      return;
    }

    // GPS obligatorio: no permitir entrega sin ubicación capturada
    if (!gps.latitude) {
      toast.error('Se requiere ubicación GPS para registrar la entrega. Activa tu GPS y presiona REINTENTAR.');
      return;
    }

    if (shouldCollectDeliveryFee && feePaymentMethod === 'efectivo' && (!data.deliveryFeeCollected || parseFloat(data.deliveryFeeCollected) <= 0)) {
      toast.error('Debe ingresar el valor del domicilio cobrado');
      return;
    }

    setUploading(true);
    try {
      const deliveryData = {
        orderId: order.id,
        amountReceived: trustedDelivery ? 0 : parseFloat(data.amountReceived || 0),
        deliveryFeeCollected: parseFloat(data.deliveryFeeCollected || 0),
        // Nuevos campos: métodos de pago seleccionados por el mensajero
        productPaymentMethod,
        deliveryFeePaymentMethod: feePaymentMethod,
        transferAmount: parseFloat(data.transferAmount || 0), // Enviar el monto real incluso con trustedDelivery
        transferBank: data.transferBank || null,
        transferReference: data.transferReference || null,
        transferDate: data.transferDate || null,
        // Entrega con confianza
        trustedDelivery: !!trustedDelivery,
        authorizedByName: trustedDelivery ? String(authorizedByName).trim() : null,
        trustNote: trustedDelivery ? trustNote : null,
        // Enviar flag de pendiente de comprobante para asegurar consistencia en backend
        isPendingEvidence: order?.is_pending_payment_evidence ? true : false,
        // Payment evidences are now handled by MultiplePaymentEvidenceUpload component
        paymentPhoto: null,
        deliveryPhoto: deliveryPhoto?.file,
        notes: data.notes,
        // Pasar GPS capturado en el modal
        latitude: gps.latitude,
        longitude: gps.longitude
      };

      await onConfirm(deliveryData);
      handleClose();
      toast.success('Entrega registrada exitosamente');
    } catch (error) {
      console.error('Error registrando entrega:', error);
      toast.error('Error al registrar la entrega');
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto flex flex-col">
        {/* Header Sticky */}
        <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">
            Registrar Entrega
            <span className="ml-2 text-sm font-normal text-gray-500">#{order?.order_number}</span>
          </h2>
          <button
            onClick={handleClose}
            className="p-2 -mr-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <Icons.X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {/* Tarjeta Cliente */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 space-y-3">
            <div className="flex items-start gap-3">
              <Icons.User className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-gray-900">{order?.customer_name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Icons.Phone className="w-3.5 h-3.5 text-gray-400" />
                  <p className="text-xs text-gray-600">{order?.customer_phone}</p>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3 border-t border-gray-200 pt-3">
              <Icons.MapPin className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
              <p className="text-sm text-gray-700 leading-snug">{order?.customer_address}</p>
            </div>

            <div className="flex items-center gap-3 border-t border-gray-200 pt-3">
              <Icons.CreditCard className="w-5 h-5 text-gray-400 shrink-0" />
              <p className="text-sm text-gray-700 capitalize">
                Pago: <span className="font-medium">{order?.payment_method || 'Efectivo'}</span>
              </p>
            </div>

            {(order?.notes || order?.siigo_observations) && (
              <div className="flex items-start gap-3 border-t border-gray-200 pt-3">
                <Icons.FileText className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
                <p className="text-xs text-gray-600 leading-snug italic whitespace-pre-line">
                  {order?.notes || order?.siigo_observations}
                </p>
              </div>
            )}
          </div>

          {/* Estado GPS */}
          <div className={`p-4 rounded-xl border flex items-center justify-between transition-colors ${gps.latitude ? 'bg-green-50 border-green-200' :
            gps.loading ? 'bg-blue-50 border-blue-200' :
              'bg-amber-50 border-amber-200'
            }`}>
            <div className="flex items-center gap-3">
              {gps.latitude ? (
                <div className="p-2 bg-green-500 rounded-full">
                  <Icons.MapPin className="w-4 h-4 text-white" />
                </div>
              ) : gps.loading ? (
                <div className="p-2 bg-blue-500 rounded-full animate-pulse">
                  <Icons.Loader2 className="w-4 h-4 text-white animate-spin" />
                </div>
              ) : (
                <div className="p-2 bg-amber-500 rounded-full">
                  <Icons.MapPinOff className="w-4 h-4 text-white" />
                </div>
              )}
              <div>
                <p className={`text-xs font-bold uppercase tracking-tight ${gps.latitude ? 'text-green-700' :
                  gps.loading ? 'text-blue-700' :
                    'text-amber-700'
                  }`}>
                  {gps.latitude ? 'Ubicación Capturada' :
                    gps.loading ? 'Obteniendo Ubicación...' :
                      'Ubicación No Disponible'}
                </p>
                <p className="text-[10px] text-gray-500 mt-0.5">
                  {gps.latitude ? `${gps.latitude.toFixed(5)}, ${gps.longitude.toFixed(5)}` :
                    gps.loading ? 'Esto puede tardar unos segundos...' :
                      gps.error || 'Verifica los permisos de tu navegador'}
                </p>
              </div>
            </div>
            {!gps.latitude && !gps.loading && (
              <button
                type="button"
                onClick={captureGPS}
                className="px-3 py-1 bg-white border border-amber-200 text-amber-700 text-[10px] font-bold rounded-lg hover:bg-amber-100 transition-colors"
              >
                REINTENTAR
              </button>
            )}
          </div>

          {/* Monto Hero */}
          <div className="text-center py-2">
            {totalDue > 0 ? (
              <>
                <p className="text-xs font-semibold text-red-500 uppercase tracking-wider mb-1">
                  {productDue === 0 && shippingDue > 0 ? 'Cobrar Solo Envío' : 'Monto a Cobrar'}
                </p>
                <p className="text-4xl font-extrabold text-gray-900 tracking-tight">
                  ${(totalDue).toLocaleString('es-CO', { minimumFractionDigits: 0 })}
                </p>
              </>
            ) : (
              <>
                <p className="text-xs font-semibold text-green-600 uppercase tracking-wider mb-1">
                  {isCreditOrder(order) ? 'Cliente a Crédito' : 'Pedido Pagado'}
                </p>
                <p className="text-4xl font-extrabold text-green-600 tracking-tight">
                  SIN COBRO
                </p>
              </>
            )}
          </div>

          {!isNoCharge && showPaymentSections && (
            <div className="space-y-4">
              <p className="text-sm font-medium text-gray-700">1. ¿Cómo recibiste el pago?</p>

              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  disabled={!requiresPayment}
                  onClick={() => handleProductQuickSelect('efectivo')}
                  className={`
                    relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all
                    ${productChoice === 'efectivo'
                      ? 'border-green-500 bg-green-50 text-green-700 ring-1 ring-green-500'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-green-200 hover:bg-gray-50'}
                  `}
                >
                  <Icons.Banknote className={`w-8 h-8 mb-2 ${productChoice === 'efectivo' ? 'text-green-600' : 'text-gray-400'}`} />
                  <span className="text-sm font-bold">Efectivo</span>
                  <span className="text-xs opacity-75 mt-1">${formatCOP(expectedAmount)}</span>
                  {productChoice === 'efectivo' && (
                    <div className="absolute top-2 right-2">
                      <Icons.CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                  )}
                </button>

                <button
                  type="button"
                  disabled={!requiresPayment}
                  onClick={() => handleProductQuickSelect('transferencia')}
                  className={`
                    relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all
                    ${productChoice === 'transferencia'
                      ? 'border-blue-500 bg-blue-50 text-blue-700 ring-1 ring-blue-500'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-blue-200 hover:bg-gray-50'}
                  `}
                >
                  <Icons.Landmark className={`w-8 h-8 mb-2 ${productChoice === 'transferencia' ? 'text-blue-600' : 'text-gray-400'}`} />
                  <span className="text-sm font-bold">Transferencia</span>
                  <span className="text-xs opacity-75 mt-1">Nequi/Banco...</span>
                  {productChoice === 'transferencia' && (
                    <div className="absolute top-2 right-2">
                      <Icons.CheckCircle2 className="w-5 h-5 text-blue-600" />
                    </div>
                  )}
                </button>
              </div>

              {/* Select de respaldo oculto visualmente pero funcional si algo falla */}
              <div className="hidden">
                <select {...register('productPaymentMethod')}>
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                </select>
              </div>
            </div>
          )}

          {/* Formulario Efectivo */}
          {(!isNoCharge && totalDue > 0 && expectedAmount > 0 && productPaymentMethod === 'efectivo') && (
            <div className="bg-green-50/50 rounded-xl p-4 border border-green-100 space-y-4 animate-in slide-in-from-top-2">
              <div>
                <label className="block text-xs font-bold text-green-900 uppercase tracking-wide mb-1.5">
                  Monto Recibido en Efectivo *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-green-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    className={`block w-full pl-7 pr-3 py-3 rounded-lg border-2 bg-white focus:ring-0 text-gray-900 placeholder-gray-400 text-lg font-semibold
                      ${errors.amountReceived ? 'border-red-300 focus:border-red-500' : 'border-green-100 focus:border-green-500'}`}
                    {...register('amountReceived', (!isNoCharge && expectedAmount > 0) ? {
                      required: 'Ingrese el monto',
                      min: { value: 0.01, message: '> 0' }
                    } : {})}
                  />
                </div>
                {errors.amountReceived && (
                  <p className="mt-1 text-xs text-red-500 font-medium flex items-center gap-1">
                    <Icons.AlertCircle className="w-3 h-3" /> {errors.amountReceived.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-green-600">
                  El valor esperado es ${formatCOP(expectedAmount)} (tolerancia +/- 200)
                </p>
              </div>
            </div>
          )}

          {/* Formulario Transferencia */}
          {(!isNoCharge && totalDue > 0 && expectedAmount > 0 && productPaymentMethod === 'transferencia') && (
            <div className="bg-blue-50/50 rounded-xl p-4 border border-blue-100 space-y-4 animate-in slide-in-from-top-2">
              <div>
                <label className="block text-xs font-bold text-blue-900 uppercase tracking-wide mb-1.5">
                  Monto Transferido *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-blue-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    className={`block w-full pl-7 pr-3 py-3 rounded-lg border-2 bg-white focus:ring-0 text-gray-900 placeholder-gray-400 text-lg font-semibold
                      ${errors.transferAmount ? 'border-red-300 focus:border-red-500' : 'border-blue-100 focus:border-blue-500'}`}
                    {...register('transferAmount', (!isNoCharge && expectedAmount > 0) ? {
                      required: 'Ingrese el monto',
                      min: { value: 0.01, message: '> 0' }
                    } : {})}
                  />
                </div>
                {errors.transferAmount && (
                  <p className="mt-1 text-xs text-red-500 font-medium flex items-center gap-1">
                    <Icons.AlertCircle className="w-3 h-3" /> {errors.transferAmount.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-blue-600">
                  El valor esperado es ${formatCOP(expectedAmount)}
                </p>
              </div>

              {/* Entrega con confianza */}
              <label className="flex items-start gap-3 p-3 bg-white rounded-lg border border-blue-100 cursor-pointer hover:border-blue-300 transition-colors">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    {...register('trustedDelivery')}
                  />
                </div>
                <div className="text-sm">
                  <span className="font-medium text-blue-900 block flex items-center gap-1">
                    <Icons.ShieldCheck className="w-4 h-4 text-blue-600" />
                    Entrega Con Confianza
                  </span>
                  <span className="text-blue-500 text-xs">
                    Si el cliente promete pagar después o el banco está caído.
                  </span>
                </div>
              </label>

              {trustedDelivery && (
                <div className="space-y-3 pl-2 border-l-2 border-blue-200 ml-1">
                  <div>
                    <input
                      placeholder="¿Quién autoriza? (Nombre)"
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      {...register('authorizedByName', { required: trustedDelivery })}
                    />
                  </div>
                  <div>
                    <input
                      placeholder="Nota / Motivo"
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      {...register('trustNote', { required: trustedDelivery })}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Secciones de Evidencia */}
          <div className="space-y-6">
            {/* Foto Pago */}
            {productPaymentMethod !== 'efectivo' && (
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Soportes de Pago
                </label>
                <MultiplePaymentEvidenceUpload
                  orderId={order?.id}
                  showExistingEvidences={true}
                />
              </div>
            )}

            {/* Foto Entrega */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Evidencia de Entrega <span className="text-red-500">*</span>
              </label>
              <div className={`
                  relative border-2 border-dashed rounded-xl p-6 transition-all group
                  ${deliveryPhoto ? 'border-green-500 bg-green-50/30' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}
                `}>
                {deliveryPhoto ? (
                  <div className="relative">
                    <img
                      src={deliveryPhoto.preview}
                      className="w-full h-48 object-cover rounded-lg shadow-sm"
                      alt="Preview"
                    />
                    <button
                      type="button"
                      onClick={() => setDeliveryPhoto(null)}
                      className="absolute -top-3 -right-3 bg-red-500 text-white p-1.5 rounded-full shadow-md hover:bg-red-600 transition-colors"
                    >
                      <Icons.X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="flex flex-col items-center justify-center p-3 rounded-xl border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer">
                        <Icons.Camera className="w-6 h-6 text-gray-600 mb-1" />
                        <span className="text-xs font-bold text-gray-700">Cámara</span>
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          className="sr-only"
                          onChange={(e) => handleFileChange(e, 'delivery')}
                        />
                      </label>
                      <label className="flex flex-col items-center justify-center p-3 rounded-xl border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer">
                        <Icons.Image className="w-6 h-6 text-gray-600 mb-1" />
                        <span className="text-xs font-bold text-gray-700">Galería</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="sr-only"
                          onChange={(e) => handleFileChange(e, 'delivery')}
                        />
                      </label>
                    </div>
                    <p className="text-xs text-center text-gray-500 mt-2">v.2.0 - Obligatorio</p>
                  </>
                )}
              </div>
            </div>

            {/* Notas */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notas Adicionales</label>
              <textarea
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:border-blue-500 focus:ring-0 resize-none"
                rows="3"
                placeholder="¿Alguna observación sobre la entrega?"
                {...register('notes')}
              ></textarea>
            </div>

            {/* Footer Sticky */}
            <div className="pt-4 mt-6 border-t border-gray-100 flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 py-3 px-4 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 active:bg-gray-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={uploading}
                className="flex-1 py-3 px-4 bg-gray-900 text-white rounded-xl font-bold shadow-lg shadow-gray-900/20 hover:bg-gray-800 active:scale-[0.98] transition-all disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center gap-2"
              >
                {uploading ? (
                  <>
                    <Icons.Loader2 className="w-5 h-5 animate-spin" />
                    Registrando...
                  </>
                ) : (
                  'Confirmar Entrega'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeliveryRegistrationModal;
